import { LogoText } from '@/components/displays/LogoText/LogoText';
import { Button, Flex, Stack, Text, Title } from '@mantine/core';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { signOut } from '@/auth/AuthUtils';
import { routeLogin } from '@/auth/constants/authSettings';

import { unlockApp, useAppLock } from './appLock';
import { biometricMethodName, unlockWithBiometrics } from './biometricAuth';
import { getEnrollment } from './biometricEnrollment';
import { BiometricIcon } from './BiometricIcon';

export function BiometricLockScreen() {
	const locked = useAppLock(state => state.locked);
	return locked ? <LockOverlay /> : null;
}

function LockOverlay() {
	const navigate = useNavigate();
	const [enrollment] = useState(getEnrollment);
	const [isUnlocking, setIsUnlocking] = useState(false);

	const signInWithPassword = () => {
		unlockApp();
		signOut();
		navigate(routeLogin, { replace: true });
	};

	const unlock = async () => {
		setIsUnlocking(true);
		const result = await unlockWithBiometrics();
		if (result === 'busy') {
			return;
		}
		setIsUnlocking(false);
		if (result === 'revoked') {
			signInWithPassword();
		}
	};

	useEffect(() => {
		void unlock();
		// Prompt once, when the lock appears; afterwards the button retries.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!enrollment) {
		return null;
	}

	const method = biometricMethodName(enrollment.kind);

	return (
		<Flex
			role="dialog"
			aria-modal="true"
			aria-label={t('biometric.lockTitle')}
			direction="column"
			justify="center"
			align="center"
			pos="fixed"
			inset={0}
			bg="var(--mantine-color-body)"
			style={{
				zIndex: 10000,
				paddingTop: 'var(--safe-area-top)',
				paddingBottom: 'var(--safe-area-bottom)',
			}}
		>
			<Stack align="center" gap="md" px="lg">
				<LogoText maw={180} mb="lg" />
				<Title order={4}>{t('biometric.lockTitle')}</Title>
				{enrollment.email && (
					<Text c="dimmed" size="sm">
						{enrollment.email}
					</Text>
				)}
				<Button
					leftSection={<BiometricIcon kind={enrollment.kind} size={22} />}
					loading={isUnlocking}
					onClick={() => void unlock()}
				>
					{t('biometric.unlockWith', { method })}
				</Button>
				<Button variant="subtle" onClick={signInWithPassword}>
					{t('biometric.usePassword')}
				</Button>
			</Stack>
		</Flex>
	);
}
